/*
 * Product page conversion components: quantity breaks, delivery estimate, sticky add-to-cart.
 * No dependencies besides Dawn's global pub/sub (constants.js + pubsub.js), which every page loads.
 */

// Dawn publishes variantChange with the original section id, so the main product and a quick-add
// modal of the same section look identical. Remember which product-info the shopper last changed
// and only react when the event comes from our own product-info.
let conversionVariantSource = null;
document.addEventListener(
  'change',
  (event) => {
    const info = event.target.closest?.('product-info');
    if (info && event.target.closest('variant-selects')) conversionVariantSource = info;
  },
  true
);
const isOwnVariantChange = (element) => {
  const info = element.closest('product-info');
  return !conversionVariantSource || !info || conversionVariantSource === info;
};

// One money formatter for every conversion component, fed by the locale and currency Liquid renders.
window.conversionFormatMoney =
  window.conversionFormatMoney ||
  ((cents, currency, locale) => {
    try {
      return new Intl.NumberFormat(locale || document.documentElement.lang || undefined, {
        style: 'currency',
        currency: currency || window.Shopify?.currency?.active,
      }).format(cents / 100);
    } catch (error) {
      return (cents / 100).toFixed(2);
    }
  });

const conversionSubscribe = (eventName, callback) =>
  typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined'
    ? subscribe(PUB_SUB_EVENTS[eventName], callback)
    : () => {};

if (!customElements.get('quantity-breaks')) {
  customElements.define(
    'quantity-breaks',
    class QuantityBreaks extends HTMLElement {
      connectedCallback() {
        this.sectionId = this.dataset.section;
        this.formId = this.dataset.form;
        this.mainVariant = {
          id: this.dataset.variantId,
          price: parseInt(this.dataset.variantPrice, 10) || 0,
          title: this.dataset.variantTitle || '',
        };
        this.serverPrices = new WeakMap();
        this.cacheServerPrices();

        this.onChange = this.onChange.bind(this);
        this.onClick = this.onClick.bind(this);
        this.onSubmit = this.onSubmit.bind(this);
        this.addEventListener('change', this.onChange);
        this.addEventListener('click', this.onClick);
        document.addEventListener('submit', this.onSubmit, true);
        this.unsubscribe = conversionSubscribe('variantChange', this.onVariantChange.bind(this));
        this.sync(false);
      }

      disconnectedCallback() {
        this.removeEventListener('change', this.onChange);
        this.removeEventListener('click', this.onClick);
        document.removeEventListener('submit', this.onSubmit, true);
        this.unsubscribe?.();
      }

      get inputs() {
        return Array.from(this.querySelectorAll('.quantity-breaks__input'));
      }

      get selected() {
        return this.querySelector('.quantity-breaks__input:checked') || this.inputs[0];
      }

      get quantity() {
        return parseInt(this.selected?.value, 10) || 1;
      }

      cacheServerPrices() {
        this.inputs.forEach((input) => {
          const prices = input.parentElement.querySelector('.quantity-breaks__prices');
          if (prices) this.serverPrices.set(input, { html: prices.innerHTML, total: input.dataset.total });
        });
      }

      onChange(event) {
        const target = event.target;
        if (target.classList.contains('quantity-breaks__input')) {
          this.sync(true);
        } else if (target.matches('[data-unit-select]')) {
          target.dataset.custom = 'true';
          const unit = target.closest('[data-unit]');
          const label = unit?.querySelector('[data-unit-value]');
          if (label) label.textContent = target.selectedOptions[0]?.textContent.trim() || '';
          this.sync(false);
        }
      }

      onClick(event) {
        const button = event.target.closest('[data-unit-change]');
        if (!button) return;
        const wrapper = button.parentElement.querySelector('[data-unit-select-wrapper]');
        if (!wrapper) return;
        const open = wrapper.hasAttribute('hidden');
        wrapper.toggleAttribute('hidden', !open);
        button.setAttribute('aria-expanded', String(open));
        if (open) wrapper.querySelector('select')?.focus();
      }

      units() {
        return Array.from(this.querySelectorAll('[data-unit]'));
      }

      // Variant id for every unit in the selected tier (unit 1 always follows the main picker).
      unitVariants() {
        const quantity = this.quantity;
        const result = [];
        for (let n = 1; n <= quantity; n += 1) {
          const select = this.querySelector(`[data-unit="${n}"] [data-unit-select]`);
          if (n === 1 || !select) {
            result.push({ id: String(this.mainVariant.id), price: this.mainVariant.price });
          } else {
            const option = select.selectedOptions[0];
            result.push({ id: String(select.value), price: parseInt(option?.dataset.price, 10) || this.mainVariant.price });
          }
        }
        return result;
      }

      isMixed() {
        return this.unitVariants().some((unit) => unit.id !== String(this.mainVariant.id));
      }

      updateUnits(quantity) {
        const container = this.querySelector('[data-units]');
        if (!container) return;
        container.toggleAttribute('hidden', quantity < 2);
        this.units().forEach((unit) => {
          const n = parseInt(unit.dataset.unit, 10);
          unit.toggleAttribute('hidden', n > quantity);
        });
      }

      followMainVariant() {
        this.units().forEach((unit) => {
          const n = parseInt(unit.dataset.unit, 10);
          const label = unit.querySelector('[data-unit-value]');
          const select = unit.querySelector('[data-unit-select]');
          if (n === 1 || !select) {
            if (label) label.textContent = this.mainVariant.title;
            return;
          }
          if (select.dataset.custom === 'true') return;
          const option = Array.from(select.options).find((o) => o.value === String(this.mainVariant.id));
          if (option) select.value = option.value;
          if (label) label.textContent = this.mainVariant.title;
        });
      }

      format(cents) {
        return window.conversionFormatMoney(cents, this.dataset.currency, this.dataset.locale);
      }

      // Restore server-rendered prices, then recalculate the selected tier when units differ in price.
      updatePrices(selected) {
        this.inputs.forEach((input) => {
          const cached = this.serverPrices.get(input);
          const prices = input.parentElement.querySelector('.quantity-breaks__prices');
          if (cached && prices && prices.innerHTML !== cached.html) prices.innerHTML = cached.html;
          if (cached) input.dataset.total = cached.total;
        });

        const units = this.unitVariants();
        // Shopify allocates the discount per cart line, so a mixed bundle (several variants = several lines)
        // has to be floored line by line to match checkout to the cent.
        const mixed = units.some((unit) => String(unit.id) !== String(this.mainVariant.id));
        if (!mixed) return selected.dataset.total;

        const discount = parseInt(selected.dataset.discount, 10) || 0;
        const full = units.reduce((sum, unit) => sum + unit.price, 0);
        const lines = new Map();
        units.forEach((unit) => lines.set(String(unit.id), (lines.get(String(unit.id)) || 0) + unit.price));
        const savings = Array.from(lines.values()).reduce((sum, lineTotal) => sum + Math.floor((lineTotal * discount) / 100), 0);
        const total = full - savings;
        const totalText = this.format(total);
        selected.dataset.total = totalText;

        const prices = selected.parentElement.querySelector('.quantity-breaks__prices');
        if (prices) {
          const price = prices.querySelector('.quantity-breaks__price');
          if (price) price.textContent = totalText;
          const fullPrice = prices.querySelector('.quantity-breaks__price-full');
          if (fullPrice) fullPrice.lastChild.textContent = this.format(full);
          const saving = prices.querySelector('.quantity-breaks__savings');
          if (saving && this.dataset.youSave) saving.textContent = this.dataset.youSave.replace('__AMOUNT__', this.format(savings));
        }
        return totalText;
      }

      updateButton(total) {
        if (this.dataset.priceInButton !== 'true') return;
        const button = document.getElementById(`ProductSubmitButton-${this.sectionId}`);
        const label = button?.querySelector('span');
        if (!button || !label || button.hasAttribute('disabled')) return;
        const base = window.variantStrings?.addToCart || label.textContent.split(' · ')[0];
        label.textContent = total ? `${base} · ${total}` : base;
      }

      sync(announce) {
        const selected = this.selected;
        if (!selected) return;
        const quantity = this.quantity;
        const hiddenInput = this.querySelector('.quantity-breaks__quantity');
        if (hiddenInput) hiddenInput.value = quantity;
        const tierInput = this.querySelector('.quantity-breaks__tier-property');
        if (tierInput) tierInput.value = quantity;

        this.updateUnits(quantity);
        const total = this.updatePrices(selected);
        this.updateButton(total);
        this.dispatchEvent(
          new CustomEvent('quantity-breaks:change', {
            bubbles: true,
            detail: { sectionId: this.sectionId, quantity, total, announce },
          })
        );
      }

      onVariantChange({ data } = {}) {
        if (!data || !isOwnVariantChange(this)) return;
        if (data.variant) {
          const price = Number(data.variant.price);
          this.mainVariant = {
            id: data.variant.id != null ? String(data.variant.id) : this.mainVariant.id,
            price: Number.isFinite(price) ? price : this.mainVariant.price,
            title: data.variant.title || this.mainVariant.title,
          };
          this.followMainVariant();
        }
        const fresh = data.html?.getElementById(this.id) || data.html?.getElementById(this.id.replace(/^quickadd-/, ''));
        const current = this.querySelector('.quantity-breaks__options');
        const updated = fresh?.querySelector('.quantity-breaks__options');
        if (current && updated) {
          const selectedIndex = this.inputs.indexOf(this.selected);
          current.innerHTML = updated.innerHTML;
          const inputs = this.inputs;
          if (selectedIndex >= 0 && inputs[selectedIndex]) inputs[selectedIndex].checked = true;
          this.serverPrices = new WeakMap();
          this.cacheServerPrices();
        }
        this.sync(false);
      }

      // Mixed bundles (e.g. two different colours) are added as several lines in one request.
      onSubmit(event) {
        const form = event.target;
        // Read the attribute: a product form has an input named "id", which shadows form.id.
        if (!(form instanceof HTMLFormElement) || form.getAttribute('id') !== this.formId || !this.isMixed()) return;
        event.preventDefault();
        event.stopImmediatePropagation();

        const productForm = form.closest('product-form');
        const submitButton = productForm?.querySelector('[type="submit"]');
        if (submitButton?.getAttribute('aria-disabled') === 'true') return;
        productForm?.handleErrorMessage?.();
        submitButton?.setAttribute('aria-disabled', 'true');
        submitButton?.classList.add('loading');
        productForm?.querySelector('.loading__spinner')?.classList.remove('hidden');

        const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        const tier = this.querySelector('.quantity-breaks__tier-property')?.value;
        const counts = new Map();
        this.unitVariants().forEach((unit) => counts.set(unit.id, (counts.get(unit.id) || 0) + 1));
        const sellingPlan = form.querySelector('[name="selling_plan"]')?.value;
        const items = Array.from(counts, ([id, quantity]) => ({
          id: Number(id),
          quantity,
          ...(sellingPlan ? { selling_plan: Number(sellingPlan) } : {}),
          ...(tier ? { properties: { _tier: tier } } : {}),
        }));
        const body = { items };
        if (cart) {
          body.sections = cart.getSectionsToRender().map((section) => section.id);
          body.sections_url = window.location.pathname;
          cart.setActiveElement?.(document.activeElement);
        }

        fetch(`${window.routes.cart_add_url}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
          body: JSON.stringify(body),
        })
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              productForm?.handleErrorMessage?.(response.description || response.message);
              if (typeof publish === 'function') {
                publish(PUB_SUB_EVENTS.cartError, {
                  source: 'product-form',
                  productVariantId: this.mainVariant.id,
                  errors: response.errors || response.description,
                  message: response.message,
                });
              }
              return;
            }
            if (!cart) {
              window.location = window.routes.cart_url;
              return;
            }
            if (typeof publish === 'function') {
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: this.mainVariant.id,
                cartData: response,
              });
            }
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => setTimeout(() => cart.renderContents({ ...response, key: response.items?.[0]?.key, id: response.items?.[0]?.id })),
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              cart.renderContents({ ...response, key: response.items?.[0]?.key, id: response.items?.[0]?.id });
            }
          })
          .catch((error) => {
            console.error(error);
            productForm?.handleErrorMessage?.(window.cartStrings?.error);
          })
          .finally(() => {
            submitButton?.classList.remove('loading');
            submitButton?.removeAttribute('aria-disabled');
            productForm?.querySelector('.loading__spinner')?.classList.add('hidden');
            if (cart && cart.classList.contains('is-empty')) cart.classList.remove('is-empty');
          });
      }
    }
  );
}

if (!customElements.get('delivery-estimate')) {
  customElements.define(
    'delivery-estimate',
    class DeliveryEstimate extends HTMLElement {
      connectedCallback() {
        const template = this.querySelector('template');
        const text = this.querySelector('.delivery-estimate__text');
        if (!template || !text) return;

        const min = parseInt(this.dataset.min, 10) || 0;
        const max = Math.max(parseInt(this.dataset.max, 10) || min, min);
        const businessDays = this.dataset.businessDays === 'true';
        const start = this.addDays(new Date(), min, businessDays);
        const end = this.addDays(new Date(), max, businessDays);

        let formatter;
        const options = { weekday: 'short', day: 'numeric', month: 'short' };
        try {
          formatter = new Intl.DateTimeFormat(this.dataset.locale || document.documentElement.lang, options);
        } catch (error) {
          formatter = new Intl.DateTimeFormat(document.documentElement.lang || undefined, options);
        }

        const html = template.innerHTML
          .replace('__START__', this.escape(formatter.format(start)))
          .replace('__END__', this.escape(formatter.format(end)));
        text.innerHTML = html;
      }

      // NZ public holidays and the main Chinese holidays (warehouse closures) that delay dispatch.
      // Extend this list each year; extra dates can be passed via data-holidays="YYYY-MM-DD,...".
      static HOLIDAYS = [
        '2026-10-26', '2026-12-25', '2026-12-28', '2027-01-01', '2027-01-04', '2027-02-08',
        '2027-02-05', '2027-02-09', '2027-02-10', '2027-02-11', '2027-02-12',
        '2027-03-26', '2027-03-29', '2027-04-05', '2027-04-26', '2027-05-03', '2027-05-04',
        '2027-05-05', '2027-06-07', '2027-06-09', '2027-06-25', '2027-09-15',
        '2027-10-01', '2027-10-04', '2027-10-05', '2027-10-06', '2027-10-07', '2027-10-25',
      ];

      isBusinessDay(date) {
        const day = date.getDay();
        if (day === 0 || day === 6) return false;
        const iso = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
        const extra = (this.dataset.holidays || '').split(',').map((d) => d.trim());
        return !DeliveryEstimate.HOLIDAYS.includes(iso) && !extra.includes(iso);
      }

      addDays(date, days, businessDays) {
        const result = new Date(date.getTime());
        if (!businessDays) {
          result.setDate(result.getDate() + days);
          return result;
        }
        let added = 0;
        while (added < days) {
          result.setDate(result.getDate() + 1);
          if (this.isBusinessDay(result)) added += 1;
        }
        return result;
      }

      escape(value) {
        const span = document.createElement('span');
        span.textContent = value;
        return span.innerHTML;
      }
    }
  );
}

if (!customElements.get('sticky-atc')) {
  customElements.define(
    'sticky-atc',
    class StickyAtc extends HTMLElement {
      connectedCallback() {
        // Quick-add modals reuse the product markup; a fixed bar makes no sense there.
        if (this.closest('quick-add-modal')) {
          this.remove();
          return;
        }
        this.sectionId = this.dataset.section;
        this.button = this.querySelector('[data-sticky-button]');
        this.buttonText = this.querySelector('[data-sticky-button-text]');
        this.priceElement = this.querySelector('[data-sticky-price]');
        this.variantElement = this.querySelector('[data-sticky-variant]');
        this.imageElement = this.querySelector('[data-sticky-image]');
        this.mainButton = document.getElementById(`ProductSubmitButton-${this.sectionId}`);
        if (!this.mainButton || !this.button) return;
        this.defaultLabel = this.buttonText?.textContent?.trim();

        this.onClick = this.onClick.bind(this);
        this.onQuantityBreaks = this.onQuantityBreaks.bind(this);
        this.button.addEventListener('click', this.onClick);
        document.addEventListener('quantity-breaks:change', this.onQuantityBreaks);
        this.unsubscribe = conversionSubscribe('variantChange', this.onVariantChange.bind(this));

        this.observeMainButton();
        this.mirrorMainButton();

        // The breaks component may already have announced its default before we connected.
        const breaks = document.getElementById(`QuantityBreaks-${this.sectionId}`);
        const selected = breaks?.querySelector('.quantity-breaks__input:checked');
        if (selected?.dataset.total) this.setPrice(selected.dataset.total);
      }

      disconnectedCallback() {
        this.button?.removeEventListener('click', this.onClick);
        document.removeEventListener('quantity-breaks:change', this.onQuantityBreaks);
        this.unsubscribe?.();
        this.observer?.disconnect();
        this.mutationObserver?.disconnect();
        if (this.onScroll) window.removeEventListener('scroll', this.onScroll);
      }

      observeMainButton() {
        this.observer = new IntersectionObserver(
          ([entry]) => {
            // Show the bar only once the main button has scrolled above the viewport.
            const scrolledPast = !entry.isIntersecting && entry.boundingClientRect.top < 0;
            this.toggle(scrolledPast);
          },
          { threshold: 0 }
        );
        this.observer.observe(this.mainButton);

        // IntersectionObserver does not fire when a jump (e.g. scroll to top) skips the intersecting
        // state, so re-check the button position on scroll as well.
        this.onScroll = () => {
          if (this.scrollFrame) return;
          this.scrollFrame = requestAnimationFrame(() => {
            this.scrollFrame = null;
            this.toggle(this.mainButton.getBoundingClientRect().bottom < 0);
          });
        };
        window.addEventListener('scroll', this.onScroll, { passive: true });

        this.mutationObserver = new MutationObserver(() => this.mirrorMainButton());
        this.mutationObserver.observe(this.mainButton, {
          attributes: true,
          attributeFilter: ['disabled', 'aria-disabled'],
          childList: true,
          subtree: true,
          characterData: true,
        });
      }

      toggle(visible) {
        this.classList.toggle('is-visible', visible);
        if (visible) {
          this.removeAttribute('inert');
        } else {
          this.setAttribute('inert', '');
        }
      }

      mirrorMainButton() {
        const disabled = this.mainButton.hasAttribute('disabled');
        this.button.disabled = disabled;
        if (disabled) {
          const label = this.mainButton.querySelector('span')?.textContent?.trim();
          if (label) this.buttonText.textContent = label;
        } else if (this.dataset.action === 'scroll') {
          if (this.defaultLabel) this.buttonText.textContent = this.defaultLabel;
        } else {
          const label = this.mainButton.querySelector('span')?.textContent?.trim().split(' · ')[0];
          this.buttonText.textContent = window.variantStrings?.addToCart || label || this.buttonText.textContent;
        }
      }

      onClick() {
        if (this.dataset.action === 'scroll') {
          const target =
            document.getElementById(`variant-selects-${this.sectionId}`) ||
            document.getElementById(`QuantityBreaks-${this.sectionId}`) ||
            this.mainButton;
          target.scrollIntoView({ behavior: 'smooth', block: 'center' });
          return;
        }
        this.mainButton.click();
      }

      onQuantityBreaks(event) {
        if (event.detail?.sectionId !== this.sectionId) return;
        if (event.detail.total) this.setPrice(event.detail.total);
      }

      onVariantChange({ data } = {}) {
        if (!data || data.sectionId !== this.sectionId || !isOwnVariantChange(this)) return;
        const variant = data.variant;
        if (!variant) return;

        if (this.variantElement) this.variantElement.textContent = variant.title === 'Default Title' ? '' : variant.title;

        const breaks = document.getElementById(`QuantityBreaks-${this.sectionId}`);
        const selected = breaks?.querySelector('.quantity-breaks__input:checked');
        if (selected?.dataset.total) {
          this.setPrice(selected.dataset.total);
        } else {
          const priceContainer = data.html?.getElementById(`price-${this.sectionId}`);
          const onSale = priceContainer?.querySelector('.price--on-sale');
          const freshPrice = onSale
            ? priceContainer.querySelector('.price__sale .price-item--last')
            : priceContainer?.querySelector('.price__regular .price-item--regular');
          if (freshPrice) this.setPrice(freshPrice.textContent.trim());
        }

        const image = variant.featured_media?.preview_image;
        if (image?.src && this.imageElement) {
          const url = new URL(image.src, window.location.origin);
          url.searchParams.set('width', '120');
          this.imageElement.src = url.toString();
          this.imageElement.removeAttribute('srcset');
        }
      }

      setPrice(text) {
        if (this.priceElement && text) this.priceElement.textContent = text;
      }
    }
  );
}
